import React from 'react';
import { Search, X, Building2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectOption } from '../ui';

export interface LocationFilters {
  search: string;
  organization_id?: string;
}

interface Organization {
  id: string;
  name: string;
}

interface FilterPanelProps {
  filters: LocationFilters;
  onFiltersChange: (filters: LocationFilters) => void;
  resultCount: number;
  organizations?: Organization[];
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  resultCount,
  organizations = [],
}) => {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFiltersChange({ ...filters, organization_id: value || undefined });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      organization_id: undefined,
    });
  };

  const hasActiveFilters = !!filters.search || !!filters.organization_id;

  const getSelectedOrganizationName = () => {
    if (!filters.organization_id) return null;
    const org = organizations.find(o => o.id === filters.organization_id);
    return org?.name || filters.organization_id;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Строка поиска и фильтр организаций */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по названию или адресу..."
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-64">
              <Select
                placeholder="Организация"
                value={filters.organization_id || ''}
                onChange={handleOrganizationChange}
              >
                <SelectOption value="">Все организации</SelectOption>
                {organizations.map((org) => (
                  <SelectOption key={org.id} value={org.id}>
                    {org.name}
                  </SelectOption>
                ))}
              </Select>
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Очистить
              </Button>
            )}
          </div>

          {/* Результаты */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>
              Найдено локаций: <strong>{resultCount}</strong>
            </span>
            {hasActiveFilters && (
              <div className="flex gap-2">
                {filters.search && (
                  <Badge variant="outline">
                    Поиск: "{filters.search}"
                  </Badge>
                )}
                {filters.organization_id && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {getSelectedOrganizationName()}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};